import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdSearch, MdVisibility, MdFileDownload } from 'react-icons/md';
import '../master/MasterData.css';

interface StockTransaction {
  transactionId: number;
  transactionNo: string;
  transactionDate: string;
  transactionType: 'IN' | 'OUT';
  movementType: 'PURCHASE' | 'SALES' | 'PRODUCTION' | 'TRANSFER' | 'RETURN' | 'ADJUSTMENT';
  itemCode: string;
  itemName: string;
  quantity: number;
  unit: string;
  referenceNo: string;
  location: string;
  performedBy: string;
  remarks: string;
  status: 'DRAFT' | 'CONFIRMED' | 'COMPLETED';
}

const StockTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<StockTransaction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('ALL');
  const [showModal, setShowModal] = useState(false);
  const [viewingTransaction, setViewingTransaction] = useState<StockTransaction | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    transactionType: 'IN' as 'IN' | 'OUT',
    movementType: 'PURCHASE' as 'PURCHASE' | 'SALES' | 'PRODUCTION' | 'TRANSFER' | 'RETURN' | 'ADJUSTMENT',
    itemCode: '',
    itemName: '',
    quantity: 0,
    unit: 'pcs',
    referenceNo: '',
    location: '',
    remarks: ''
  });

  useEffect(() => {
    // TODO: Fetch stock transactions from API
    setTransactions([
      {
        transactionId: 1,
        transactionNo: 'STK-IN-2025-0001',
        transactionDate: '2025-02-15',
        transactionType: 'IN',
        movementType: 'PURCHASE',
        itemCode: 'RM-001',
        itemName: 'Polyurethane Foam - High Density',
        quantity: 2000,
        unit: 'kg',
        referenceNo: 'GR-2025-0001',
        location: 'Warehouse A - Raw Materials',
        performedBy: 'John Warehouse',
        remarks: 'Purchase receipt from Foam Suppliers Inc.',
        status: 'COMPLETED'
      },
      {
        transactionId: 2,
        transactionNo: 'STK-OUT-2025-0001',
        transactionDate: '2025-02-16',
        transactionType: 'OUT',
        movementType: 'PRODUCTION',
        itemCode: 'RM-001',
        itemName: 'Polyurethane Foam - High Density',
        quantity: 500,
        unit: 'kg',
        referenceNo: 'PROD-2025-0001',
        location: 'Warehouse A - Raw Materials',
        performedBy: 'Production Team',
        remarks: 'Material issued for production order',
        status: 'COMPLETED'
      },
      {
        transactionId: 3,
        transactionNo: 'STK-IN-2025-0002',
        transactionDate: '2025-02-18',
        transactionType: 'IN',
        movementType: 'PRODUCTION',
        itemCode: 'FG-001',
        itemName: 'Executive Office Chair - Black',
        quantity: 100,
        unit: 'pcs',
        referenceNo: 'PROD-2025-0001',
        location: 'Warehouse B - Finished Goods',
        performedBy: 'Production Team',
        remarks: 'Completed production batch',
        status: 'COMPLETED'
      },
      {
        transactionId: 4,
        transactionNo: 'STK-OUT-2025-0002',
        transactionDate: '2025-02-20',
        transactionType: 'OUT',
        movementType: 'SALES',
        itemCode: 'FG-001',
        itemName: 'Executive Office Chair - Black',
        quantity: 50,
        unit: 'pcs',
        referenceNo: 'DR-2025-0001',
        location: 'Warehouse B - Finished Goods',
        performedBy: 'Sales Department',
        remarks: 'Delivery for SO-2025-0001',
        status: 'COMPLETED'
      },
      {
        transactionId: 5,
        transactionNo: 'STK-IN-2025-0003',
        transactionDate: '2025-02-22',
        transactionType: 'IN',
        movementType: 'RETURN',
        itemCode: 'FG-002',
        itemName: '3-Seater Sofa - Grey',
        quantity: 2,
        unit: 'pcs',
        referenceNo: 'RET-2025-0001',
        location: 'Warehouse B - Finished Goods',
        performedBy: 'QC Department',
        remarks: 'Customer return - Quality issue',
        status: 'CONFIRMED'
      }
    ]);
  }, []);

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch =
      transaction.transactionNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.referenceNo.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType =
      filterType === 'ALL' ||
      (filterType === 'IN' && transaction.transactionType === 'IN') ||
      (filterType === 'OUT' && transaction.transactionType === 'OUT');

    return matchesSearch && matchesType;
  });

  const handleView = (transaction: StockTransaction) => {
    setViewingTransaction(transaction);
    setShowModal(true);
  };

  const handleAdd = () => {
    setFormData({
      transactionType: 'IN',
      movementType: 'PURCHASE',
      itemCode: '',
      itemName: '',
      quantity: 0,
      unit: 'pcs',
      referenceNo: '',
      location: '',
      remarks: ''
    });
    setShowAddModal(true);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // Generate transaction number
    const type = formData.transactionType;
    const count = transactions.filter(t => t.transactionType === type).length + 1;
    const transactionNo = `STK-${type}-2025-${count.toString().padStart(4, '0')}`;

    const newTransaction: StockTransaction = {
      transactionId: transactions.length + 1,
      transactionNo,
      transactionDate: new Date().toISOString().split('T')[0],
      transactionType: formData.transactionType,
      movementType: formData.movementType,
      itemCode: formData.itemCode,
      itemName: formData.itemName,
      quantity: formData.quantity,
      unit: formData.unit,
      referenceNo: formData.referenceNo,
      location: formData.location,
      performedBy: 'Current User', // TODO: Get from auth context
      remarks: formData.remarks,
      status: 'DRAFT'
    };

    setTransactions([newTransaction, ...transactions]);
    setShowAddModal(false);
    alert('Transaction created successfully!');
  };

  const getTransactionTypeBadge = (type: string) => {
    return type === 'IN'
      ? <span className="badge badge-success">STOCK IN</span>
      : <span className="badge badge-danger">STOCK OUT</span>;
  };

  const getMovementTypeBadge = (type: string) => {
    switch (type) {
      case 'PURCHASE': return <span className="badge badge-primary">PURCHASE</span>;
      case 'SALES': return <span className="badge badge-info">SALES</span>;
      case 'PRODUCTION': return <span className="badge badge-warning">PRODUCTION</span>;
      case 'TRANSFER': return <span className="badge badge-secondary">TRANSFER</span>;
      case 'RETURN': return <span className="badge badge-danger">RETURN</span>;
      case 'ADJUSTMENT': return <span className="badge badge-gray">ADJUSTMENT</span>;
      default: return <span className="badge badge-secondary">{type}</span>;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'badge-success';
      case 'CONFIRMED': return 'badge-info';
      default: return 'badge-warning';
    }
  };

  const handleExport = () => {
    alert('Export to Excel functionality would be implemented here');
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Stock In/Out Transactions</h2>
            <p className="subtitle">Track all inventory movements and transactions</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handleExport}>
              <MdFileDownload /> Export
            </button>
            <button className="btn btn-primary" onClick={handleAdd}>
              <MdAdd /> New Transaction
            </button>
          </div>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-tabs">
              <button
                className={`filter-tab ${filterType === 'ALL' ? 'active' : ''}`}
                onClick={() => setFilterType('ALL')}
              >
                All Transactions
              </button>
              <button
                className={`filter-tab ${filterType === 'IN' ? 'active' : ''}`}
                onClick={() => setFilterType('IN')}
              >
                Stock In
              </button>
              <button
                className={`filter-tab ${filterType === 'OUT' ? 'active' : ''}`}
                onClick={() => setFilterType('OUT')}
              >
                Stock Out
              </button>
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Transaction No</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Movement</th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Quantity</th>
                  <th>Reference No</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map(transaction => (
                  <tr key={transaction.transactionId}>
                    <td className="code">{transaction.transactionNo}</td>
                    <td>{new Date(transaction.transactionDate).toLocaleDateString('en-US')}</td>
                    <td>{getTransactionTypeBadge(transaction.transactionType)}</td>
                    <td>{getMovementTypeBadge(transaction.movementType)}</td>
                    <td className="code">{transaction.itemCode}</td>
                    <td>{transaction.itemName}</td>
                    <td style={{ textAlign: 'center', fontWeight: 'bold', color: transaction.transactionType === 'IN' ? '#28a745' : '#dc3545' }}>
                      {transaction.transactionType === 'IN' ? '+' : '-'}{transaction.quantity.toLocaleString()} {transaction.unit}
                    </td>
                    <td className="code">{transaction.referenceNo}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(transaction)}
                          title="View Details"
                        >
                          <MdVisibility />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredTransactions.length === 0 && (
              <div className="no-data">
                <p>No transactions found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && viewingTransaction && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transaction Details</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Transaction No</label>
                  <input type="text" value={viewingTransaction.transactionNo} disabled />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="text" value={new Date(viewingTransaction.transactionDate).toLocaleDateString('en-US')} disabled />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Transaction Type</label>
                  <input type="text" value={viewingTransaction.transactionType} disabled />
                </div>
                <div className="form-group">
                  <label>Movement Type</label>
                  <input type="text" value={viewingTransaction.movementType} disabled />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Item Code</label>
                  <input type="text" value={viewingTransaction.itemCode} disabled />
                </div>
                <div className="form-group">
                  <label>Item Name</label>
                  <input type="text" value={viewingTransaction.itemName} disabled />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Quantity</label>
                  <input type="text" value={`${viewingTransaction.quantity} ${viewingTransaction.unit}`} disabled />
                </div>
                <div className="form-group">
                  <label>Reference No</label>
                  <input type="text" value={viewingTransaction.referenceNo} disabled />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Location</label>
                  <input type="text" value={viewingTransaction.location} disabled />
                </div>
                <div className="form-group">
                  <label>Performed By</label>
                  <input type="text" value={viewingTransaction.performedBy} disabled />
                </div>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea value={viewingTransaction.remarks} disabled />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Stock Transaction</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Transaction Type <span style={{ color: 'red' }}>*</span></label>
                  <select
                    value={formData.transactionType}
                    onChange={(e) => handleFormChange('transactionType', e.target.value)}
                  >
                    <option value="IN">Stock IN</option>
                    <option value="OUT">Stock OUT</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Movement Type <span style={{ color: 'red' }}>*</span></label>
                  <select
                    value={formData.movementType}
                    onChange={(e) => handleFormChange('movementType', e.target.value)}
                  >
                    {formData.transactionType === 'IN' ? (
                      <>
                        <option value="PURCHASE">Purchase</option>
                        <option value="PRODUCTION">Production</option>
                        <option value="RETURN">Return</option>
                        <option value="TRANSFER">Transfer In</option>
                        <option value="ADJUSTMENT">Adjustment</option>
                      </>
                    ) : (
                      <>
                        <option value="SALES">Sales</option>
                        <option value="PRODUCTION">Production</option>
                        <option value="TRANSFER">Transfer Out</option>
                        <option value="ADJUSTMENT">Adjustment</option>
                      </>
                    )}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Item Code <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    value={formData.itemCode}
                    onChange={(e) => handleFormChange('itemCode', e.target.value)}
                    placeholder="Enter item code"
                  />
                </div>
                <div className="form-group">
                  <label>Item Name <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    value={formData.itemName}
                    onChange={(e) => handleFormChange('itemName', e.target.value)}
                    placeholder="Enter item name"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Quantity <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => handleFormChange('quantity', Number(e.target.value))}
                    placeholder="Enter quantity"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Unit <span style={{ color: 'red' }}>*</span></label>
                  <select
                    value={formData.unit}
                    onChange={(e) => handleFormChange('unit', e.target.value)}
                  >
                    <option value="pcs">Pieces</option>
                    <option value="kg">Kilograms</option>
                    <option value="meters">Meters</option>
                    <option value="liters">Liters</option>
                    <option value="box">Box</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Reference No</label>
                  <input
                    type="text"
                    value={formData.referenceNo}
                    onChange={(e) => handleFormChange('referenceNo', e.target.value)}
                    placeholder="Enter reference number"
                  />
                </div>
                <div className="form-group">
                  <label>Location <span style={{ color: 'red' }}>*</span></label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleFormChange('location', e.target.value)}
                    placeholder="Enter location"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => handleFormChange('remarks', e.target.value)}
                  placeholder="Enter any additional remarks"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!formData.itemCode || !formData.itemName || !formData.quantity || !formData.location}
              >
                Save Transaction
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default StockTransactions;
